<template>
  <div class="flex flex-col flex-1 min-h-0 h-full w-full">
    <div v-if="isDesktopLayout" class="flex flex-1 min-h-0 h-full bg-white overflow-hidden font-sans mt-6">
      <!-- Columna Izquierda: Header + Lista (con scroll) -->
      <div :class="[
        'flex flex-col overflow-y-auto transition-all duration-300',
        showDesktopMap ? 'w-3/5' : 'w-full'
      ]">
        <!-- Header con SEO + Lead Magnet + Buscador -->
        <header class="flex-shrink-0 bg-white relative z-30">
        <!-- Fila 1: Breadcrumbs -->
        <div class="px-6 pt-4 pb-2">
          <nav v-if="breadcrumbItems.length > 1" aria-label="Breadcrumb">
            <ol class="flex items-center text-xs text-gray-400">
              <li v-for="(item, index) in breadcrumbItems" :key="index" class="flex items-center">
                <router-link v-if="item.to && index < breadcrumbItems.length - 1" :to="item.to"
                  class="hover:text-indigo-600 transition-colors">
                  {{ item.name }}
                </router-link>
                <span v-else class="text-gray-900 font-medium">{{ item.name }}</span>
                <svg v-if="index < breadcrumbItems.length - 1" class="h-3 w-3 mx-1.5 text-gray-300"
                  fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clip-rule="evenodd" />
                </svg>
              </li>
            </ol>
          </nav>

          <!-- H1 SEO (B2C) -->
          <div class="flex-1 min-w-0">
            <h1 class="text-lg md:text-xl font-bold text-gray-900 mb-1">
              {{ seoH1Title }}
            </h1>
            <!-- Trust signals inline y compactos -->
            <p class="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
              <span class="inline-flex items-center gap-1">
                <svg class="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"/>
                </svg>
                {{ $t('colivingSearch.trustSignals.verified') }}
              </span>
              <span class="text-gray-300">·</span>
              <span>{{ $t('colivingSearch.trustSignals.noCommissions') }}</span>
            </p>
            <p class="text-xs text-gray-500 mt-1">
              <router-link
                to="/leads-marketplace"
                class="text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
              >
                {{ $t('colivingSearch.operatorBanner.link') }}
              </router-link>
            </p>
            <!-- Enlace inverso blog → marketplace (internal linking SEO) -->
            <div v-if="currentCityBlogLink" class="mt-2 space-y-1">
              <p>
                <router-link
                  :to="`/${currentCityBlogLink.blogSlug}`"
                  class="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                >
                  <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  {{ currentCityBlogLink.linkLabel }}
                </router-link>
              </p>
              <p
                v-for="extra in (currentCityBlogLink.extraLinks || [])"
                :key="extra.blogSlug"
                class="pl-5"
              >
                <router-link
                  :to="`/${extra.blogSlug}`"
                  class="text-xs font-medium text-indigo-600/90 hover:text-indigo-800 hover:underline"
                >
                  {{ extra.linkLabel }}
                </router-link>
              </p>
            </div>
          </div>
        </div>

        <!-- Fila 2: Buscador centrado y prominente -->
        <div class="px-6 pb-3 flex justify-start">
          <div class="w-full max-w-3xl">
            <ColivingSearchBar 
              v-model:selected-locations="selectedLocations"
              v-model:max-price="filters.maxPrice"
              v-model:selected-top-city="selectedTopCity"
              :available-locations="availableLocations"
              :top-city-options="topCityOptions"
              :is-loading="isLoading"
              @submit="$emit('submit')"
              @apply-location-filter="$emit('apply-location-filter')"
            />
          </div>
        </div>

        <!-- Fila 2.3: Guardar búsqueda / Alerta -->
        <div class="px-6 pb-2 flex justify-start">
          <button
            @click="$emit('save-search')"
            class="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-indigo-600 transition-colors"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            Crear alerta de nuevos alojamientos
          </button>
        </div>

        <!-- Fila 2.5: City Quick-Select — solo España, solo cuando no hay ciudad activa.
             Orden basado en datos reales de GSC mayo 2026 (impresiones + CTR).
             Son router-links (navegación), no filtros, para mantener el SEO de /encuentratucoliving. -->
        <div v-if="regionId === 'es' && !currentCity" class="px-6 pb-2 flex justify-start">
          <div class="w-full max-w-3xl flex items-center gap-1.5 overflow-x-auto" style="scrollbar-width: none;">            <router-link
              v-for="city in quickCities"
              :key="city.slug"
              :to="`/encuentratucoliving/${city.slug}`"
              class="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-full border text-xs font-medium transition-all whitespace-nowrap border-gray-200 bg-white text-gray-600 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
            >
              <svg class="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
              </svg>
              {{ city.label }}
            </router-link>
          </div>
        </div>

        <!-- Fila 2.5b: London neighbourhood chips — UK, página London o sin barrio activo.
             Orden: GSC impressions + search demand (mayo 2026). -->
        <div v-if="regionId === 'uk' && (!currentCity || currentCity === 'London')" class="px-6 pb-2 flex justify-start">
          <div class="w-full max-w-3xl flex items-center gap-1.5 overflow-x-auto" style="scrollbar-width: none;">
            <router-link
              v-for="area in quickLondonAreas"
              :key="area.slug"
              :to="`/findyourcoliving/${area.slug}`"
              class="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-full border text-xs font-medium transition-all whitespace-nowrap border-gray-200 bg-white text-gray-600 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
            >
              <svg class="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
              </svg>
              {{ area.label }}
            </router-link>
          </div>
        </div>

        <!-- Fila 2.5c: NYC borough/neighbourhood chips — US, página NYC o sin barrio activo.
             Orden: GSC impressions (Brooklyn 77, Manhattan 91). -->
        <div v-if="regionId === 'us' && (!currentCity || currentCity === 'New York')" class="px-6 pb-2 flex justify-start">
          <div class="w-full max-w-3xl flex items-center gap-1.5 overflow-x-auto" style="scrollbar-width: none;">
            <router-link
              v-for="area in quickNYCAreas"
              :key="area.slug"
              :to="`/findyourcoliving/${area.slug}`"
              class="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-full border text-xs font-medium transition-all whitespace-nowrap border-gray-200 bg-white text-gray-600 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
            >
              <svg class="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
              </svg>
              {{ area.label }}
            </router-link>
          </div>
        </div>

        <!-- Fila 3: Filtros rápidos (5 visibles + Más filtros) -->
        <div class="px-6 pb-3 flex justify-start">
          <div class="w-full max-w-3xl flex items-start gap-2 flex-wrap">
            <SearchQuickFiltersPills
              :filters="filters"
              show-price
              :price-options="pillPriceOptionsForQuick"
              :price-label="pillPriceLabel"
              :stay-options="pillStayOptions"
              :stay-label="pillStayLabel"
              :active-dropdown="activePillDropdown"
              :show-more-panel="showAdvancedFilters"
              :more-filters-active="pillMoreActive"
              :more-filters-count="pillMoreCount"
              :has-active-filters="getActiveFiltersCount() > 0"
              @toggle-dropdown="$emit('toggle-pill-dropdown', $event)"
              @select-price="$emit('select-pill-price', $event)"
              @select-stay="$emit('select-pill-stay', $event)"
              @quick-toggle="$emit('quick-toggle', $event)"
              @toggle-more="showAdvancedFilters = !showAdvancedFilters; activePillDropdown = null"
              @clear="$emit('clear-filters')"
            />

            <!-- Servicios para empresas (B2B, separado de filtros B2C) -->
            <span class="text-gray-200 text-lg flex-shrink-0 select-none hidden sm:inline">|</span>
            <button
              type="button"
              @click="$emit('partner-services')"
              class="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-dashed border-indigo-300 bg-indigo-50/60 text-indigo-600 text-sm font-medium transition-all whitespace-nowrap select-none flex-shrink-0 hover:bg-indigo-50 hover:border-indigo-400"
            >
              <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {{ t('navigation.partnerServices') }}
            </button>
          </div>
        </div>

        <!-- PREVIEW AdSense (local) — desactivado para prod
        <div v-if="showAdPreview" class="px-6 pb-3">
          <GoogleAdPlaceholder variant="leaderboard" />
        </div>
        -->

      </header>

        <!-- CTA leads sticky (desktop) -->
        <SearchLeadCaptureBanner
          :visible="showLeadCaptureCta"
          :city="leadCaptureCity || currentCity || ''"
          :operators-count="operatorsInCityCount"
          :has-session-profile="hasLeadSessionProfile"
          sticky
          wrapper-class="mx-6"
          @action="$emit('lead-search')"
          @change-data="$emit('lead-change-data')"
        />

        <!-- SearchResultsList directamente en la columna con scroll -->
        <SearchResultsList ref="searchResultsListRef" :is-loading="isLoading" :search-error="searchError"
          :search-results="searchResults" :search-performed="searchPerformed"
          :highlighted-result-id="highlightedResultId" :property-location="locationDisplay"
          :default-sort-order="filters.sortBy" :is-coliving-search="true" :saved-coliving-ids="savedColivingIds"
          :has-more-pages="hasMorePages" :is-loading-more="isLoadingMore" :total-results="totalResults"
          :list-page="listPage"
          :show-advanced-filters="showAdvancedFilters" :active-filters-count="getActiveFiltersCount()"
          :map-visible="showDesktopMap"
          @card-mouseenter="$emit('highlight-marker', $event)" @card-mouseleave="$emit('unhighlight-marker')" @sort-change="$emit('sort-change', $event)"
          @toggleSave="$emit('toggle-save', $event)" @update:list-page="$emit('list-page-change', $event)" @toggle-filters="showAdvancedFilters = !showAdvancedFilters"
          @request-scraped-lead="$emit('lead-listing', $event)"
          @request-hive-lead="$emit('lead-listing', $event)">
              
              <!-- Panel Más filtros (solo secundarios) -->
              <template #filters-panel>
                <SearchMoreFiltersPanel
                  v-model:filters="filters"
                  :available-providers="availableProviders"
                  size="sm"
                />
              </template>

              <!-- Botones de acción (slot separado, siempre visible) -->
              <template #filters-actions>
                <div class="flex justify-end gap-2">
              <button type="button" @click="$emit('clear-filters')"
                    class="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors">
                    {{ $t('colivingSearch.advancedFilters.actions.clear') }}
              </button>
              <button type="button" @click="$emit('apply-advanced-filters')"
                    class="px-4 py-1.5 text-xs font-medium text-indigo-600 bg-white border border-indigo-200 rounded-full hover:bg-indigo-50 hover:shadow-md shadow-sm transition-all">
                    {{ $t('colivingSearch.advancedFilters.actions.apply') }}
              </button>
            </div>
              </template>
          </SearchResultsList>

          <!-- PREVIEW AdSense (local) — desactivado para prod
          <div v-if="showAdPreview && searchPerformed && searchResults.length > 0" class="px-6 pb-4">
            <GoogleAdPlaceholder variant="multiplex" />
          </div>
          -->

          <SearchSeoSection
            v-model:show-open="showSeoContent"
            :search-performed="searchPerformed"
            :results-count="searchResults.length"
            :search-performed-or-city="searchPerformed || !!currentCity"
            :current-city="currentCity"
            :region-id="regionId"
            :is-provider-search="isProviderSearch"
            :current-provider="currentProvider"
            :current-provider-content="currentProviderContent"
            :current-city-content="currentCityContent"
            :seo-h2-title="seoH2Title"
            :visible-faqs="visibleFaqs"
            :is-student-mode="isStudentMode"
            :student-hub-cities="studentHubCities"
            :student-hub-seo-content="studentHubSeoContent"
          />

      </div>
      <!-- Fin de columna izquierda -->

      <!-- Columna Derecha: Map (fijo sin scroll) -->
      <div v-if="showDesktopMap && deferDesktopMap" class="w-2/5 flex-shrink-0 transition-all duration-300 sticky top-16 h-screen self-start">
        <div class="h-full p-3 flex flex-col gap-3">
          <div class="flex-1 min-h-0 rounded-2xl overflow-hidden shadow-sm border border-gray-200">
            <Suspense>
              <SearchResultsMap
                :key="`desktop-map-${regionId}`"
                :region-id="regionId"
                :search-results="mapMarkerResults"
                :highlighted-result-id="highlightedResultId"
                :total-results="totalResults"
                :current-user-id="currentUser?.uid"
                :is-loading="isLoading"
                :is-loading-map-results="isLoadingMapResults"
                @marker-click="$emit('highlight-result', $event)"
                @bounds-update="$emit('map-bounds-update', $event)"
                @request-scraped-lead="$emit('lead-listing', $event)"
                @request-hive-lead="$emit('lead-listing', $event)"
              />
              <template #fallback>
                <div class="h-full w-full animate-pulse bg-gray-100" aria-hidden="true" />
              </template>
            </Suspense>
          </div>
          <!-- PREVIEW AdSense (local) — desactivado para prod
          <div v-if="showAdPreview" class="flex-shrink-0">
            <GoogleAdPlaceholder variant="sidebar" />
          </div>
          -->
        </div>
      </div>

      <!-- Botón Toggle Mapa (flotante a la derecha) - Oculto cuando filtros avanzados o dropdown de ubicación están abiertos -->
      <button v-show="!showAdvancedFilters && !showLocationDropdown" type="button"
        @mouseenter="!showDesktopMap && $emit('prefetch-map')"
        @focus="!showDesktopMap && $emit('prefetch-map')"
        @click="showDesktopMap = !showDesktopMap" :class="[
          'fixed top-1/2 -translate-y-1/2 z-30 p-2 rounded-l-xl shadow-md transition-all duration-300',
          showDesktopMap
            ? 'right-[40%] bg-white border border-r-0 border-gray-200 text-gray-400 hover:text-gray-900 hover:bg-gray-50'
            : 'right-0 bg-gray-900 text-white hover:bg-gray-800'
        ]" :title="showDesktopMap ? 'Ocultar mapa' : 'Mostrar mapa'">
        <!-- Icono cuando el mapa está visible (flecha hacia derecha para ocultar) -->
        <svg v-if="showDesktopMap" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20"
          fill="currentColor">
          <path fill-rule="evenodd"
            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
            clip-rule="evenodd" />
        </svg>
        <!-- Icono cuando el mapa está oculto (icono de mapa para mostrar) -->
        <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd"
            d="M12 1.586l-4 4v12.828l4-4V1.586zM3.707 3.293A1 1 0 002 4v10a1 1 0 00.293.707L6 18.414V5.586L3.707 3.293zM17.707 5.293L14 1.586v12.828l2.293 2.293A1 1 0 0018 16V6a1 1 0 00-.293-.707z"
            clip-rule="evenodd" />
        </svg>
      </button>
    </div>
    <!-- Fin del layout desktop -->

    <!-- Layout para Pantallas Móviles (< lg) -->
    <div v-else class="flex flex-col flex-1 min-h-0 h-full bg-gray-50 font-sans">

      <!-- H1 SEO (mobile) — visible; guías de ciudad opcionales debajo -->
      <div class="px-3 pt-2 pb-1 bg-white border-b border-gray-100">
        <h1 class="text-base font-bold text-gray-900 leading-snug">
          {{ seoH1Title }}
        </h1>
        <div v-if="currentCityBlogLink" class="flex items-center gap-2 mt-1">
          <button
            type="button"
            @click="showMobileBlogLinks = !showMobileBlogLinks"
            class="text-xs text-gray-400 hover:text-indigo-600 transition-colors flex items-center gap-0.5"
          >
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            {{ $t('colivingSearch.mobileGuides') || 'Guías' }}
            <svg :class="['w-3 h-3 transition-transform', showMobileBlogLinks ? 'rotate-180' : '']" fill="none" viewBox="0 0 20 20" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        <div v-if="currentCityBlogLink && showMobileBlogLinks" class="mt-1.5 space-y-0.5">
          <p>
            <router-link
              :to="`/${currentCityBlogLink.blogSlug}`"
              class="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
            >
              {{ currentCityBlogLink.linkLabel }}
            </router-link>
          </p>
          <p
            v-for="extra in (currentCityBlogLink.extraLinks || [])"
            :key="extra.blogSlug"
          >
            <router-link
              :to="`/${extra.blogSlug}`"
              class="text-xs font-medium text-indigo-600/90 hover:text-indigo-800"
            >
              {{ extra.linkLabel }}
            </router-link>
          </p>
        </div>
      </div>

      <!-- Filter Bar (Mobile) - Toggle Mapa + Ubicación + Filtros avanzados -->
      <div class="sticky top-0 z-10 bg-white px-3 py-2.5 border-b border-gray-100 shadow-sm">
        <div class="flex items-center gap-2 relative">
          <!-- Botón Toggle Mapa/Lista (Mobile) -->
          <button type="button" @click="isMobileMapViewActive = !isMobileMapViewActive"
            class="flex-shrink-0 h-10 w-10 bg-gray-50 border border-gray-200 rounded-full flex items-center justify-center hover:border-indigo-300 hover:bg-indigo-50 transition-all duration-200"
            :class="{ 'border-indigo-500 bg-indigo-50 text-indigo-600': isMobileMapViewActive }"
            :aria-label="isMobileMapViewActive ? 'Ver lista' : 'Ver mapa'">
            <!-- Icono Mapa (cuando se muestra la lista) -->
            <svg v-if="!isMobileMapViewActive" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
              fill="currentColor" class="h-5 w-5 text-gray-500">
              <path fill-rule="evenodd"
                d="M8.161 2.58a1.875 1.875 0 0 1 1.678 0l4.993 2.498c.106.052.23.052.336 0l3.869-1.935A1.875 1.875 0 0 1 21.75 4.82v12.485c0 .71-.401 1.36-1.037 1.677l-4.875 2.437a1.875 1.875 0 0 1-1.676 0l-4.994-2.497a.375.375 0 0 0-.336 0l-3.868 1.935A1.875 1.875 0 0 1 2.25 19.18V6.695c0-.71.401-1.36 1.036-1.677l4.875-2.437ZM9 6a.75.75 0 0 1 .75.75V15a.75.75 0 0 1-1.5 0V6.75A.75.75 0 0 1 9 6Zm6.75 3a.75.75 0 0 0-1.5 0v8.25a.75.75 0 0 0 1.5 0V9Z"
                clip-rule="evenodd" />
            </svg>
            <!-- Icono Lista (cuando se muestra el mapa) -->
            <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-5 w-5">
              <path fill-rule="evenodd"
                d="M3 6.75A.75.75 0 0 1 3.75 6h16.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 6.75ZM3 12a.75.75 0 0 1 .75-.75h16.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 12Zm0 5.25a.75.75 0 0 1 .75-.75h16.5a.75.75 0 0 1 0 1.5H3.75a.75.75 0 0 1-.75-.75Z"
                clip-rule="evenodd" />
            </svg>
          </button>

          <!-- Selector de ubicación (Dropdown) -->
          <button type="button"
            @click="showLocationDropdownMobile = !showLocationDropdownMobile; if (showLocationDropdownMobile) showAdvancedFilters = false"
            class="flex-1 h-10 bg-gray-50 border border-gray-200 rounded-full px-3 text-sm text-left flex items-center justify-between hover:border-indigo-300 transition-all duration-200"
            :class="{ 'border-indigo-500 bg-indigo-50': showLocationDropdownMobile || selectedLocations.length > 0 }"
            data-location-button-mobile>
            <div class="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4"
                :class="selectedLocations.length > 0 ? 'text-indigo-600' : 'text-gray-400'" viewBox="0 0 20 20"
                fill="currentColor">
                <path fill-rule="evenodd"
                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                  clip-rule="evenodd" />
              </svg>
              <span :class="(selectedLocations.length > 0 || selectedTopCity) ? 'text-indigo-700 font-medium' : 'text-gray-500'"
                class="truncate">
                <template v-if="selectedLocations.length > 0">{{ getSelectedLocationsDisplay() }}</template>
                <template v-else-if="selectedTopCity === 'new-york'">🇺🇸 New York</template>
                <template v-else-if="selectedTopCity === 'london'">🇬🇧 London</template>
                <template v-else>{{ $t('colivingSearch.advancedFilters.location.where') }}</template>
              </span>
              <span v-if="selectedLocations.length > 0"
                class="px-1.5 py-0.5 text-xs font-semibold text-white bg-indigo-600 rounded-full min-w-[18px] text-center">
                {{ selectedLocations.length }}
              </span>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 text-gray-400 flex-shrink-0 transition-transform duration-200"
              :class="{ 'rotate-180': showLocationDropdownMobile }" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clip-rule="evenodd" />
            </svg>
          </button>
        </div>

        <!-- Filtros rápidos mobile: precio + duración + estudiantes + parejas + más -->
        <div v-if="!showLocationDropdownMobile" class="pt-2">
          <SearchQuickFiltersPills
            :filters="filters"
            show-price
            :price-options="pillPriceOptionsForQuick"
            :price-label="pillPriceLabel"
            :stay-options="pillStayOptions"
            :stay-label="pillStayLabel"
            :active-dropdown="activePillDropdown"
            :show-more-panel="showAdvancedFilters"
            :more-filters-active="pillMoreActive"
            :more-filters-count="pillMoreCount"
            :has-active-filters="getActiveFiltersCount() > 0"
            @toggle-dropdown="$emit('toggle-pill-dropdown', $event)"
            @select-price="$emit('select-pill-price', $event)"
            @select-stay="$emit('select-pill-stay', $event)"
            @quick-toggle="$emit('quick-toggle', $event)"
            @toggle-more="showAdvancedFilters = !showAdvancedFilters; activePillDropdown = null; showLocationDropdownMobile = false"
            @clear="$emit('clear-filters')"
          />
        </div>

        <!-- Panel de Ubicaciones (Mobile) - Mismo estilo que filtros avanzados -->
        <div v-if="showLocationDropdownMobile" class="bg-white border-b border-gray-200" data-location-panel-mobile>
          <div class="px-3 py-4 bg-gray-50 space-y-4 max-h-[60vh] overflow-y-auto">

            <!-- Toggle New York / London (solo regiones US y UK) -->
            <template v-if="topCityOptions">
              <div>
                <label class="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">{{ regionId === 'es' ? 'Ciudad' : 'City' }}</label>
                <div class="flex gap-2">
                  <button
                    v-for="option in topCityOptions"
                    :key="option.id"
                    type="button"
                    @click="selectedTopCity = option.id; selectedLocations = []"
                    class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border text-sm font-medium transition-all duration-200"
                    :class="selectedTopCity === option.id
                      ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-indigo-300'">
                    <span>{{ option.flag }}</span>
                    <span>{{ option.label }}</span>
                  </button>
                </div>
              </div>

              <!-- Barrios de la ciudad seleccionada -->
              <div class="border-t border-gray-200 pt-3">
                <label class="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">{{ $t('colivingSearch.advancedFilters.location.neighborhood') }}</label>
                <div class="grid grid-cols-2 gap-x-3 gap-y-1">
                  <label v-for="location in availableLocations" :key="location"
                    class="flex items-center cursor-pointer py-2 hover:bg-white rounded px-2 -mx-2">
                    <input type="checkbox" :value="location" v-model="selectedLocations"
                      class="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
                    <span class="ml-2 text-sm text-gray-700">{{ location }}</span>
                  </label>
                </div>
              </div>
            </template>

            <!-- Vista estándar ES: Todas las ciudades + lista -->
            <template v-else>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('colivingSearch.advancedFilters.location.selectCities') }}</label>
                <label class="flex items-center cursor-pointer py-2 hover:bg-white rounded px-2 -mx-2">
                  <input type="checkbox" :checked="selectedLocations.length === 0" @change="selectedLocations = []"
                    class="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
                  <span class="ml-3 text-base font-medium text-gray-700">{{ $t('colivingSearch.advancedFilters.location.allCities') }}</span>
                </label>
              </div>

              <div class="border-t border-gray-200 pt-3">
                <div class="grid grid-cols-2 gap-x-3 gap-y-1">
                  <label v-for="location in availableLocations" :key="location"
                    class="flex items-center cursor-pointer py-2 hover:bg-white rounded px-2 -mx-2">
                    <input type="checkbox" :value="location" v-model="selectedLocations"
                      class="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
                    <span class="ml-2 text-sm text-gray-700">{{ location }}</span>
                  </label>
                </div>
              </div>
            </template>

            <!-- Botones de acción -->
            <div class="flex gap-2 pt-2">
              <button type="button" @click="selectedLocations = []; showLocationDropdownMobile = false"
                class="flex-1 px-4 py-3 text-base font-medium text-gray-600 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors">
                {{ $t('colivingSearch.advancedFilters.actions.clear') }}
              </button>
              <button type="button" @click="$emit('apply-location-filter-mobile')"
                class="flex-1 px-4 py-3 text-base font-medium text-indigo-600 bg-white border border-indigo-200 rounded-full hover:bg-indigo-50 hover:shadow-md shadow-sm transition-all">
                {{ $t('colivingSearch.advancedFilters.actions.apply') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Panel Más filtros (Mobile) — solo secundarios -->
        <div v-if="showAdvancedFilters" class="bg-white border-b border-gray-200">
          <div class="px-3 py-4 bg-gray-50 max-h-[70vh] overflow-y-auto">
            <SearchMoreFiltersPanel
              v-model:filters="filters"
              :available-providers="availableProviders"
              size="md"
            />
            <div class="flex gap-2 pt-4 mt-2 border-t border-gray-200">
              <button type="button" @click="$emit('clear-filters')"
                class="flex-1 px-4 py-3 text-base font-medium text-gray-600 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors">
                {{ $t('colivingSearch.advancedFilters.actions.clear') }}
              </button>
              <button type="button" @click="$emit('apply-advanced-filters')"
                class="flex-1 px-4 py-3 text-base font-medium text-indigo-600 bg-white border border-indigo-200 rounded-full hover:bg-indigo-50 hover:shadow-md shadow-sm transition-all">
                {{ $t('colivingSearch.advancedFilters.actions.apply') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Results List Area (Mobile) -->
      <div
        class="flex-1 overflow-y-auto"
        :class="{ 'overflow-hidden': isMobileMapViewActive }"
      >
        <template v-if="isMobileMapViewActive">
          <Suspense>
            <SearchResultsMap
              ref="searchResultsMapRefMobile"
              :key="`mobile-map-${regionId}`"
              :region-id="regionId"
              :search-results="mapMarkerResults"
              :highlighted-result-id="highlightedResultId"
              :total-results="totalResults"
              :current-user-id="currentUser?.uid"
              :is-loading="isLoading"
              :is-loading-map-results="isLoadingMapResults"
              @marker-click="$emit('mobile-marker-click', $event)"
              @bounds-update="$emit('mobile-map-bounds-update', $event)"
              @request-scraped-lead="$emit('lead-listing', $event)"
              @request-hive-lead="$emit('lead-listing', $event)"
              class="w-full h-full"
            />
            <template #fallback>
              <div class="w-full h-full animate-pulse bg-gray-100" aria-hidden="true" />
            </template>
          </Suspense>
        </template>
        <template v-else>
          <SearchResultsList ref="searchResultsListRefMobile" :is-loading="isLoading" :search-error="searchError"
            :search-results="searchResults" :search-performed="searchPerformed"
            :highlighted-result-id="highlightedResultId" :property-location="locationDisplay"
            :default-sort-order="filters.sortBy" :is-coliving-search="true" :saved-coliving-ids="savedColivingIds"
            :has-more-pages="hasMorePages" :is-loading-more="isLoadingMore" :total-results="totalResults"
            :list-page="listPage"
            :show-advanced-filters="false" :active-filters-count="getActiveFiltersCount()"
            @card-mouseenter="$emit('highlight-marker', $event)" @card-mouseleave="$emit('unhighlight-marker')" @sort-change="$emit('sort-change', $event)"
            @toggleSave="$emit('toggle-save', $event)" @update:list-page="$emit('list-page-change', $event)"
            @request-scraped-lead="$emit('lead-listing', $event)"
            @request-hive-lead="$emit('lead-listing', $event)" class="w-full h-full" :isMobile="true">
            <template #lead-capture>
              <SearchLeadCaptureBanner
                :visible="showLeadCaptureCta"
                :city="leadCaptureCity || currentCity || ''"
                :operators-count="operatorsInCityCount"
                :has-session-profile="hasLeadSessionProfile"
                @action="$emit('lead-search')"
                @change-data="$emit('lead-change-data')"
              />
            </template>
          </SearchResultsList>
        </template>
      </div>

      <!-- Bottom Search Button (Mobile) - DESACTIVADO: movido a navbar y filter bar -->
      <!-- 
      <div class="fixed bottom-0 left-0 right-0 z-10 bg-white/95 backdrop-blur-sm p-3 border-t border-gray-200 shadow-up">
        <div class="flex items-center gap-3"> 
          <button 
            type="button"
            @click="openUpgradeModal"
            class="flex-grow h-12 px-4 rounded-xl flex items-center bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow hover:shadow-md hover:from-indigo-700 hover:to-indigo-800 transition-all duration-150"
          >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-5 w-5 text-white mr-2">
          <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
        </svg>
            <span class="font-semibold text-sm whitespace-nowrap">Añade tu coliving</span>
          </button>
          
          <button
            type="button"
            @click="isMobileMapViewActive = !isMobileMapViewActive"
            class="flex-shrink-0 h-12 w-12 rounded-full bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 shadow flex items-center justify-center transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500"
            :aria-label="isMobileMapViewActive ? 'Ver lista' : 'Ver mapa'"
          >
            <svg v-if="!isMobileMapViewActive" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-6">
              <path fill-rule="evenodd" d="M8.161 2.58a1.875 1.875 0 0 1 1.678 0l4.993 2.498c.106.052.23.052.336 0l3.869-1.935A1.875 1.875 0 0 1 21.75 4.82v12.485c0 .71-.401 1.36-1.037 1.677l-4.875 2.437a1.875 1.875 0 0 1-1.676 0l-4.994-2.497a.375.375 0 0 0-.336 0l-3.868 1.935A1.875 1.875 0 0 1 2.25 19.18V6.695c0-.71.401-1.36 1.036-1.677l4.875-2.437ZM9 6a.75.75 0 0 1 .75.75V15a.75.75 0 0 1-1.5 0V6.75A.75.75 0 0 1 9 6Zm6.75 3a.75.75 0 0 0-1.5 0v8.25a.75.75 0 0 0 1.5 0V9Z" clip-rule="evenodd" />
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-6">
              <path fill-rule="evenodd" d="M3 6.75A.75.75 0 0 1 3.75 6h16.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 6.75ZM3 12a.75.75 0 0 1 .75-.75h16.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 12Zm0 5.25a.75.75 0 0 1 .75-.75h16.5a.75.75 0 0 1 0 1.5H3.75a.75.75 0 0 1-.75-.75Z" clip-rule="evenodd" />
            </svg>
          </button>

          <button
            type="button" 
            @click="performSearch"
            :disabled="isLoading"
            class="flex-shrink-0 h-12 w-12 rounded-full bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-50 hover:shadow-md shadow-sm flex items-center justify-center transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="Buscar colivings"
          >
            <div v-if="isLoading">
              <LoadingSpinner size="small" color="text-indigo-600" class="h-5 w-5" />
            </div>
            <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
              <path fill-rule="evenodd" d="M10.5 3.75a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5ZM2.25 10.5a8.25 8.25 0 1 1 14.59 5.28l4.69 4.69a.75.75 0 1 1-1.06 1.06l-4.69-4.69A8.25 8.25 0 0 1 2.25 10.5Z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
      -->

      <!-- PREVIEW AdSense (local) — desactivado para prod
      <div
        v-if="showAdPreview && !isMobileMapViewActive"
        class="fixed bottom-0 left-0 right-0 z-20 lg:hidden"
      >
        <GoogleAdPlaceholder variant="anchor" />
      </div>
      -->
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, defineAsyncComponent, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
// Preview AdSense (local): import GoogleAdPlaceholder from '../ads/GoogleAdPlaceholder.vue';
const SearchResultsMap = defineAsyncComponent(() => import('../SearchResultsMap.vue'));
import SearchResultsList from '../SearchResultsList.vue';
import SearchLeadCaptureBanner from '../SearchLeadCaptureBanner.vue';
import SearchQuickFiltersPills from '../SearchQuickFiltersPills.vue';
import SearchMoreFiltersPanel from '../SearchMoreFiltersPanel.vue';
import SearchSeoSection from './SearchSeoSection.vue';
import ColivingSearchBar from '../ColivingSearchBar.vue';
import {
  quickCities,
  quickLondonAreas,
  quickNYCAreas,
} from '@/composables/useSearchSeoContent';

const { t } = useI18n();

const selectedLocations = defineModel('selectedLocations', { type: Array, default: () => [] });
const filters = defineModel('filters', { type: Object, required: true });
const selectedTopCity = defineModel('selectedTopCity', { type: [String, null], default: null });
const showDesktopMap = defineModel('showDesktopMap', { type: Boolean, default: true });
const showAdvancedFilters = defineModel('showAdvancedFilters', { type: Boolean, default: false });
const showSeoContent = defineModel('showSeoContent', { type: Boolean, default: false });
const isMobileMapViewActive = defineModel('isMobileMapViewActive', { type: Boolean, default: false });
const activePillDropdown = defineModel('activePillDropdown', { type: [String, null], default: null });
const showLocationDropdown = defineModel('showLocationDropdown', { type: Boolean, default: false });
const showLocationDropdownMobile = defineModel('showLocationDropdownMobile', { type: Boolean, default: false });

const props = defineProps({
  // showAdPreview: { type: Boolean, default: false }, // PREVIEW AdSense (local)
  regionId: { type: String, default: 'es' },
  currentCity: { type: [String, null], default: null },
  leadCaptureCity: { type: [String, null], default: null },
  breadcrumbItems: { type: Array, default: () => [] },
  seoH1Title: { type: String, default: '' },
  currentCityBlogLink: { type: Object, default: null },
  availableLocations: { type: Array, default: () => [] },
  topCityOptions: { type: Array, default: null },
  isLoading: { type: Boolean, default: false },
  isLoadingMapResults: { type: Boolean, default: false },
  searchError: { type: String, default: '' },
  searchResults: { type: Array, default: () => [] },
  mapSearchResults: { type: Array, default: () => [] },
  searchPerformed: { type: Boolean, default: false },
  highlightedResultId: { type: [String, Number, null], default: null },
  savedColivingIds: { type: Object, default: () => new Set() },
  hasMorePages: { type: Boolean, default: false },
  isLoadingMore: { type: Boolean, default: false },
  totalResults: { type: Number, default: 0 },
  listPage: { type: Number, default: 1 },
  availableProviders: { type: Array, default: () => [] },
  pillStayOptions: { type: Array, default: () => [] },
  pillStayLabel: { type: String, default: '' },
  pillPriceOptionsForQuick: { type: Array, default: () => [] },
  pillPriceLabel: { type: String, default: '' },
  pillMoreCount: { type: Number, default: 0 },
  pillMoreActive: { type: Boolean, default: false },
  getActiveFiltersCount: { type: Function, required: true },
  showLeadCaptureCta: { type: Boolean, default: false },
  operatorsInCityCount: { type: Number, default: 0 },
  hasLeadSessionProfile: { type: Boolean, default: false },
  currentUser: { type: Object, default: null },
  isProviderSearch: { type: Boolean, default: false },
  currentProvider: { type: [String, null], default: null },
  currentProviderContent: { type: Object, default: null },
  currentCityContent: { type: Object, default: null },
  seoH2Title: { type: String, default: '' },
  visibleFaqs: { type: Array, default: () => [] },
  isStudentMode: { type: Boolean, default: false },
  studentHubCities: { type: Array, default: () => [] },
  studentHubSeoContent: { type: String, default: '' },
});

defineEmits([
  'submit',
  'save-search',
  'apply-location-filter',
  'apply-location-filter-mobile',
  'apply-advanced-filters',
  'clear-filters',
  'partner-services',
  'lead-search',
  'lead-listing',
  'lead-change-data',
  'highlight-marker',
  'unhighlight-marker',
  'sort-change',
  'toggle-save',
  'list-page-change',
  'highlight-result',
  'map-bounds-update',
  'mobile-marker-click',
  'mobile-map-bounds-update',
  'toggle-pill-dropdown',
  'select-pill-stay',
  'select-pill-price',
  'quick-toggle',
  'prefetch-map',
]);

const searchResultsListRef = ref(null);
const searchResultsListRefMobile = ref(null);
const deferDesktopMap = ref(false);
const showMobileBlogLinks = ref(false);

const isDesktopLayout = ref(
  typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : false,
);

let layoutMediaQuery = null;
let onLayoutChange = null;

onMounted(() => {
  layoutMediaQuery = window.matchMedia('(min-width: 1024px)');
  onLayoutChange = (event) => {
    isDesktopLayout.value = event.matches;
  };
  layoutMediaQuery.addEventListener('change', onLayoutChange);

  const enableMap = () => { deferDesktopMap.value = true; };
  if (showDesktopMap.value && typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(enableMap, { timeout: 2500 });
  } else if (showDesktopMap.value) {
    setTimeout(enableMap, 400);
  }
});

onUnmounted(() => {
  if (layoutMediaQuery && onLayoutChange) {
    layoutMediaQuery.removeEventListener('change', onLayoutChange);
  }
});

watch(showDesktopMap, (visible) => {
  if (visible && !deferDesktopMap.value) {
    deferDesktopMap.value = true;
  }
});

const mapMarkerResults = computed(() =>
  props.mapSearchResults.length > 0 ? props.mapSearchResults : props.searchResults,
);

defineExpose({ searchResultsListRef, searchResultsListRefMobile, isDesktopLayout });

const locationDisplay = computed(() => {
  if (selectedLocations.value.length === 0) return '';
  if (selectedLocations.value.length === 1) return selectedLocations.value[0];
  return selectedLocations.value.join(', ');
});

function getSelectedLocationsDisplay() {
  if (selectedLocations.value.length === 0) return '¿Dónde?';
  if (selectedLocations.value.length === 1) return selectedLocations.value[0];
  if (selectedLocations.value.length === 2) return selectedLocations.value.join(', ');
  return `${selectedLocations.value[0]}, ${selectedLocations.value[1]} +${selectedLocations.value.length - 2}`;
}
</script>
